import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Trash2, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";

function BlogEditor({ post, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: post?.title || "",
        slug: post?.slug || "",
        excerpt: post?.excerpt || "",
        body_markdown: post?.body_markdown || "",
        hero_eyebrow: post?.hero_eyebrow || "Insights",
        published: !!post?.published,
    });
    const [preview, setPreview] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    async function save() {
        setBusy(true); setErr(null);
        try {
            if (post?.id) {
                await api.adminUpdateBlog(post.id, form);
            } else {
                await api.adminCreateBlog(form);
            }
            onSaved?.();
            onClose();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    return (
        <div className="st-surface p-6 space-y-4" data-testid="blog-editor">
            <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>
                    {post?.id ? "Edit post" : "New post"}
                </h2>
                <div className="flex gap-2">
                    <button type="button" className="btn-ghost text-xs" onClick={onClose}>Close</button>
                    <button type="button" className="btn-outline-navy text-xs" onClick={() => setPreview(!preview)}
                            data-testid="blog-toggle-preview">
                        {preview ? "Edit" : "Preview"}
                    </button>
                    <button type="button" className="btn-royal text-xs" onClick={save} disabled={busy}
                            data-testid="blog-save">
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            {err && <div className="rounded p-2 text-sm" style={{ background: "#FEF3F2", color: "#B42318" }}>{err}</div>}

            <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-4">
                <div className="space-y-3">
                    <div>
                        <label className="st-label">Title</label>
                        <input className="st-input" value={form.title}
                               onChange={(e) => setForm({ ...form, title: e.target.value })}
                               data-testid="blog-title" />
                    </div>
                    <div>
                        <label className="st-label">Slug (leave blank to auto-generate)</label>
                        <input className="st-input" value={form.slug}
                               onChange={(e) => setForm({ ...form, slug: e.target.value })}
                               placeholder="market-mechanics-101"
                               data-testid="blog-slug" />
                    </div>
                    <div>
                        <label className="st-label">Excerpt (used in listing)</label>
                        <input className="st-input" value={form.excerpt}
                               onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                               data-testid="blog-excerpt" />
                    </div>
                    <div>
                        <label className="st-label">Body (markdown)</label>
                        {!preview ? (
                            <textarea rows={16} className="st-input"
                                      style={{ height: "auto", padding: "0.5rem 0.9rem", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
                                      value={form.body_markdown}
                                      onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
                                      data-testid="blog-body" />
                        ) : (
                            <div className="border rounded p-4 whitespace-pre-wrap" style={{ borderColor: "var(--st-line)", background: "var(--st-silver-50)" }}
                                 data-testid="blog-preview">
                                {form.body_markdown || <span style={{ color: "var(--st-silver-500)" }}>Nothing to preview.</span>}
                            </div>
                        )}
                    </div>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="st-label">Eyebrow tag</label>
                        <input className="st-input" value={form.hero_eyebrow}
                               onChange={(e) => setForm({ ...form, hero_eyebrow: e.target.value })}
                               data-testid="blog-eyebrow" />
                    </div>
                    <label className="flex items-center gap-2 text-sm" style={{ color: "var(--st-navy)" }}>
                        <input type="checkbox" checked={form.published}
                               onChange={(e) => setForm({ ...form, published: e.target.checked })}
                               data-testid="blog-published" />
                        Publish now
                    </label>
                    {post?.slug && (
                        <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                            Public URL: <span style={{ color: "var(--st-royal)" }}>/blog/{post.slug}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null); // post being edited, or {} for new
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const r = await api.adminListBlog();
            setPosts(r.posts);
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function del(id) {
        if (!window.confirm("Delete this post permanently?")) return;
        await api.adminDeleteBlog(id);
        await load();
    }

    if (editing !== null) {
        return (
            <section className="max-w-5xl mx-auto px-6 py-10">
                <BlogEditor post={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={load} />
            </section>
        );
    }

    return (
        <section className="max-w-5xl mx-auto px-6 py-10 space-y-6" data-testid="admin-blog-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Blog</div>
                    <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Posts</h1>
                    <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        Markdown posts. Published posts appear at <code>/blog</code>.
                    </p>
                </div>
                <button type="button" className="btn-royal text-sm inline-flex items-center gap-2"
                        onClick={() => setEditing({})} data-testid="new-blog-post-btn">
                    <PlusCircle size={14} /> New post
                </button>
            </div>

            {loading && <div className="text-sm">Loading…</div>}
            {!loading && posts.length === 0 && (
                <div className="st-surface p-8 text-center italic" style={{ color: "var(--st-silver-500)" }} data-testid="blog-empty">
                    No blog posts yet. Click "New post" to write the first one.
                </div>
            )}

            <ul className="space-y-2">
                {posts.map((p) => (
                    <li key={p.id} className="st-surface p-4 flex items-center justify-between gap-4" data-testid={`blog-post-${p.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-serif text-lg" style={{ color: "var(--st-navy)" }}>{p.title}</span>
                                {p.published ? (
                                    <span className="inline-flex items-center gap-1 tracking-caps text-[9px] font-bold px-2 py-0.5 rounded-full"
                                          style={{ background: "#ECFDF3", color: "#067647" }}>
                                        <Eye size={10} /> Live
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 tracking-caps text-[9px] font-bold px-2 py-0.5 rounded-full"
                                          style={{ background: "var(--st-silver-100)", color: "var(--st-ink-muted)" }}>
                                        <EyeOff size={10} /> Draft
                                    </span>
                                )}
                            </div>
                            <div className="text-xs mt-0.5" style={{ color: "var(--st-silver-500)" }}>
                                /blog/{p.slug} · Updated {new Date(p.updated_at).toLocaleDateString()}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {p.published && (
                                <Link to={`/blog/${p.slug}`} className="btn-ghost text-xs" data-testid={`view-blog-${p.id}`}>View</Link>
                            )}
                            <button type="button" className="btn-outline-navy text-xs" onClick={() => setEditing(p)} data-testid={`edit-blog-${p.id}`}>
                                Edit
                            </button>
                            <button type="button" className="btn-ghost text-xs" onClick={() => del(p.id)} data-testid={`delete-blog-${p.id}`}>
                                <Trash2 size={12} />
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}
